import React from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { GetDetailedRitualQuery, RitualState, useUpdateRitualStateMutation } from 'data/__generated__';

import { StateHistory } from 'components/DetailsView';
import { ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { StatusTag } from 'pages/Rituals/components/StatusTag';
import { confirmPopup } from 'components/Popups/Confirm';

export const StatesSection = ({
    ritual,
    isDirty,
}: {
    ritual: NonNullable<GetDetailedRitualQuery['detailedRitual']>;
    isDirty: boolean;
}) => {
    const { _id: ritualId, stateHistory, state } = ritual;

    const [updateRitualState] = useUpdateRitualStateMutation();

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Passer rituel en statut régulier',
            stateToSet: RitualState.Regular,
            isDisabled: state === RitualState.Regular,
        },
        {
            label: 'Archiver le rituel',
            stateToSet: RitualState.Archived,
            isDisabled: state === RitualState.Archived,
        },
    ];

    const handleStateUpdate = async (stateToSet: RitualState) => {
        const { data } = await updateRitualState({
            variables: {
                stateToSet,
                ritualId,
            },
        });

        if (!data) {
            throw Error("Une erreur inconnue s'est produite");
        }

        const {
            updateRitualState: { success, errors, updatedRitual },
        } = data;

        if (success && updatedRitual) {
            toast.success('Le statut du produit a bien été modifié !');
            return;
        }

        if (errors) {
            errors.forEach((error: string) => {
                toast.error(error, { autoClose: false });
                return;
            });
        }

        throw Error("Une erreur inconnue s'est produite");
    };

    const updateState = async (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        label: string,
        stateToSet: RitualState,
    ) => {
        e.preventDefault();
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
        });
        if (hasConfirmed) {
            handleStateUpdate(stateToSet);
        }
    };

    return (
        <SeparatorContainer>
            <ColumnsSectionContainer numberOfColumns={3}>
                <StateHistory label="Historique de statuts" records={formattedStateHistory} />
                <StatusTag state={state} showHeading />

                <SectionColumn>
                    {isDirty ? (
                        <WarningText>
                            ⚠️ Veuillez vous assurer d'avoir sauvegardé ou annulé vos ajustements avant de mettre à jour
                            le statut du ST
                        </WarningText>
                    ) : null}
                    <OverlaySectionColumn $isDirty={isDirty}>
                        {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                            const { stateToSet, label, isDisabled } = stateChange;

                            return (
                                <TotemPrimaryButton
                                    key={index}
                                    type="button"
                                    onClick={(event) => updateState(event, label, stateToSet)}
                                    disabled={isDisabled || isDirty}
                                >
                                    {label}
                                </TotemPrimaryButton>
                            );
                        })}
                    </OverlaySectionColumn>
                </SectionColumn>
            </ColumnsSectionContainer>
        </SeparatorContainer>
    );
};

const SeparatorContainer = styled.div`
    margin-top: 30px;
    padding-top: 15px;
    border-top: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
`;

const WarningText = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-weight: 800;
`;

const OverlaySectionColumn = styled(SectionColumn)<{ $isDirty: boolean }>`
    opacity: ${({ $isDirty }) => ($isDirty ? 0.4 : 1)};
`;
